<template>
  <v-dialog :value="value" v-on:input="$emit('input', $event)" max-width="1000px" persistent>
    <v-form ref="form" v-model="valid">
      <v-card>
        <v-toolbar>
          <v-toolbar-title class="grey--text text--darken-4"><h3 class="display-2">{{ word.text }}</h3></v-toolbar-title>
        </v-toolbar>
        <v-alert :value="showErrorOnSave" type="error">{{ errorOnSave }}</v-alert>
        <v-container grid-list-md>
          <v-layout wrap>
            <v-flex xs4>
              <TranslatedImageInput v-model="edited.image" :original="word.image" :suggest="edited.text" :key="word.id" :aspect-ratio="1"/>
            </v-flex>
            <v-flex xs8>
              <v-layout wrap>
                <v-flex xs8>
                  <v-text-field
                          v-model.trim="edited.text"
                          required
                          :rules="[required]"
                          :label="translationLabel"
                          :error-messages="errorMessages"
                          :disabled="saving"
                  ></v-text-field>
                </v-flex>
                <v-flex xs4>
                  <p v-if="hint">
                    You can use translation <a href="#" @click.prevent="useHint(hint)">"{{ hint }}"</a>
                  </p>
                  <v-progress-circular
                          v-if="!hint"
                          color="primary"
                          indeterminate
                  ></v-progress-circular>
                </v-flex>
                <v-flex xs6 v-if="edited.languageId !== 'en'">
                  <v-text-field
                          :value="english"
                          label="In English"
                          :disabled="true"
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 v-if="edited.languageId !== 'ru'">
                  <v-text-field
                          :value="russian"
                          label="In Russian"
                          :disabled="true"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12>
                  <h3 class="headline">Examples</h3>
                </v-flex>
                <v-flex xs12 >
                  <ExamplesInput v-model="edited.examples" :translation="translationForExample"></ExamplesInput>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-container>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" flat @click.native="close">Cancel</v-btn>
<!--          <v-btn color="blue darken-1" flat @click.native="saveAndClose" :loading="saving">Save</v-btn>-->
          <v-btn color="blue darken-1" flat @click.native="saveAndNext" :loading="saving">Save and Next</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import { TranslationApi, TranslatorApi } from '../common/api'
import { ADD_TRANSLATION, UPDATE_TRANSLATION } from '../store/mutation-types'
import { mapGetters, mapState } from 'vuex'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import debounce from 'lodash/debounce'
import ExamplesInput from './ExamplesInput'
import TranslatedImageInput from './TranslatedImageInput'

export default {
  components: { TranslatedImageInput, ExamplesInput },
  props: ['value', 'model'],
  data () {
    return {
      valid: false,
      saving: false,
      errorMessages: [],
      errorOnSave: 'Error has occurred while saving',
      showErrorOnSave: false,
      edited: this.defaultModel(),
      russian: '',
      english: '',
      hint: null
    }
  },
  computed: {
    word () {
      return this.model || {}
    },
    translation () {
      return this.word.translation
    },
    translationForExample () {
      return {
        source: this.sourceLanguage.id,
        target: this.targetLanguage.id,
        text: this.word.text,
        translation: this.edited.text
      }
    },
    isNew () {
      return !this.translation.id
    },
    sourceLanguage () {
      return this.dictionary.sourceLanguage || {}
    },
    targetLanguage () {
      return this.dictionary.targetLanguage || {}
    },
    targetLanguageId () {
      return this.targetLanguage.id
    },
    objectToSave () {
      return {
        ...this.edited,
        examples: this.edited.examples.map(ex => {
          return { ...ex, id: ex.id > 0 ? ex.id : null }
        })
      }
    },
    translationLabel () {
      return `Translate to ${this.targetLanguage.name}`
    },
    ...mapState([
      'dictionary'
    ]),
    ...mapGetters([
      'sourceLanguageId'
    ])
  },
  watch: {
    'edited.text' () {
      this.debouncedTranslateAll()
    },
    value () {
      this.showErrorOnSave = false
      this.$refs.form.resetValidation()
      if (!this.value) {
        return
      }
      if (this.isNew) {
        this.edited = this.defaultModel()
      } else {
        this.edited = cloneDeep(this.translation)
      }
      this.translateHint()
    }
  },
  created () {
    this.debouncedTranslateAll = debounce(this.translateAll, 300)
  },
  methods: {
    defaultModel () {
      return {
        text: '',
        languageId: this.targetLanguageId,
        image: null,
        examples: []
      }
    },
    validate () {
      return this.$refs.form.validate()
    },
    close () {
      if (this.saving) {
        return
      }
      this.$emit('input', false)
    },
    async saveAndNext () {
      try {
        const success = await this.save()
        if (success) {
          this.next()
        }
      } catch (e) {
        console.error(e)
        alert(e)
      }
    },
    async saveAndClose () {
      try {
        const success = await this.save()
        if (success) {
          this.close()
        }
      } catch (e) {
        console.error(e)
        alert(e)
      }
    },
    async save () {
      if (this.saving) {
        return false
      }
      if (!this.validate()) {
        return false
      }
      if (this.edited.examples.length === 0 && !confirm('Are you sure you want to keep the word without examples?')) {
        return false
      }
      this.showErrorOnSave = false
      this.saving = true
      try {
        const word = this.word
        if (this.isNew) {
          const { data } = await TranslationApi.create(word, this.objectToSave)
          this.$store.commit(ADD_TRANSLATION, { word, translation: data })
        } else {
          const { data } = await TranslationApi.update(word, this.objectToSave)
          this.$store.commit(UPDATE_TRANSLATION, { word, translation: data })
        }
        this.saving = false

        return true
      } catch (e) {
        this.saving = false
        this.showErrorOnSave = true
        throw e
      }
    },
    next () {
      this.$emit('next', this.model)
    },
    required (value) {
      return !!value || 'Required.'
    },
    useHint (hint) {
      this.edited.text = hint
    },
    async translateHint () {
      this.hint = null
      const getRequest = () => { return { text: this.word.text, sourceLanguage: this.sourceLanguage.id } }
      try {
        const translation = await this.translate(getRequest, this.edited.languageId)
        if (translation !== undefined) {
          this.hint = translation.toLowerCase()
        }
      } catch (e) {
        alert(e)
      }
    },
    translateAll () {
      const getRequest = () => { return { text: this.edited.text, sourceLanguage: this.edited.languageId } }
      this.russian = null
      this.english = null

      if (this.edited.languageId !== 'ru') {
        this.translate(getRequest, 'ru')
          .then((translation) => { if (translation !== undefined) { this.russian = translation } })
          .catch(e => alert(e))
      }

      if (this.edited.languageId !== 'en') {
        this.translate(getRequest, 'en')
          .then((translation) => { if (translation !== undefined) { this.english = translation } })
          .catch(e => alert(e))
      }
    },
    async translate (getRequest, target) {
      const req = getRequest()

      if (!req.text) {
        return null
      }

      try {
        const { data } = await TranslatorApi.translate(req.text, req.sourceLanguage, target)
        if (!isEqual(req, getRequest())) {
          console.log('Skip translation, because word has been changed')
          return undefined
        }
        return data.text
      } catch (e) {
        throw e
      }
    }
  }
}
</script>
